import axios from 'axios'
import { httpClientWithAuth } from '../../../../setup/axios/SetupAxios'
import { ApiResponse } from '../../../infrastracture/ApiClient'
import { GetAPIDomain } from '../../../infrastracture/Helper'
import { getConnectTokenHeaders } from '../connectTokenHeaders'
import { AuthModel } from '../models/AuthModel'
import { UserModel } from '../models/UserModel'
import type {EntraTokenResponse} from '../azureSso'
import {
  beginAzureSsoPkceLogin,
  exchangeAzureAuthorizationCode,
  getEntraLoginHint,
  refreshEntraAccessToken,
} from '../azureSso'

export {
  beginAzureSsoPkceLogin,
  exchangeAzureAuthorizationCode,
}
export type {EntraTokenResponse}

const API_URL = process.env.REACT_APP_API_URL || 'api'
const TOKEN_EXPIRY_BUFFER_SECONDS = 90
const ENTRA_SSO_FLAG = 'entra-sso'
const ENTRA_LOGIN_HINT = 'entra-login-hint'
const ENTRA_SILENT_ATTEMPT = 'entra-silent-attempt'
const SILENT_RETRY_WINDOW_MS = 5 * 60 * 1000

function storeTokenExpiry(expiresIn?: number) {
    const seconds = expiresIn ?? 3000
    const expiresAt = Date.now() + Math.max(0, seconds - TOKEN_EXPIRY_BUFFER_SECONDS) * 1000
    localStorage.setItem('expires', new Date(expiresAt).toISOString())
}

function persistEntraTokens(tokens: EntraTokenResponse) {
    localStorage.setItem('acess-token', tokens.accessToken)
    if (tokens.refreshToken) {
        localStorage.setItem('refresh-token', tokens.refreshToken)
    }
    const loginHint = getEntraLoginHint(tokens)
    if (loginHint) {
        localStorage.setItem(ENTRA_LOGIN_HINT, loginHint)
    }
    storeTokenExpiry(tokens.expiresIn)
}

export function getStoredEntraLoginHint() {
    return localStorage.getItem(ENTRA_LOGIN_HINT) || undefined
}

export function clearEntraSilentAttempt() {
    sessionStorage.removeItem(ENTRA_SILENT_ATTEMPT)
}

function currentReturnPath() {
    const {pathname, search, hash} = window.location
    if (pathname.startsWith('/login') || pathname.startsWith('/sso-login')) return '/'
    return pathname + search + hash
}

// Entra caps SPA refresh tokens at 24h from sign-in, so renew against the Entra session with a top-level redirect
async function beginSilentEntraSignIn(): Promise<boolean> {
    const tenantId = localStorage.getItem('entra-tenant-id')
    const clientId = localStorage.getItem('entra-client-id')
    const accountId = localStorage.getItem('accountId') || localStorage.getItem('tenantAccountId')
    const scope = localStorage.getItem('entra-scope') || ''

    // Bail out if a silent attempt already ran recently in this tab, so a failing sign-in can't loop
    const lastAttempt = Number(sessionStorage.getItem(ENTRA_SILENT_ATTEMPT) || 0)
    if (!tenantId || !clientId || !accountId || Date.now() - lastAttempt < SILENT_RETRY_WINDOW_MS) {
        localStorage.clear()
        return false
    }

    sessionStorage.setItem(ENTRA_SILENT_ATTEMPT, String(Date.now()))
    localStorage.removeItem('acess-token')
    localStorage.removeItem('refresh-token')

    await beginAzureSsoPkceLogin({
        tenantId,
        clientId,
        accountId,
        scope,
        loginHint: getStoredEntraLoginHint(),
        prompt: 'none',
        returnPath: currentReturnPath(),
    })

    // The page is navigating away; never settle so callers don't log out or retry in the meantime
    return new Promise<boolean>(() => {})
}

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/auth/get-user`
export const LOGIN_URL = `${API_URL}/login`
export const REGISTER_URL = `${API_URL}/auth/register`
export const REQUEST_PASSWORD_URL = `${API_URL}/auth/forgot-password`

export async function login(username: string, password: string) {
    
    let accountId = localStorage.getItem('accountId') || ''
    let formData = new URLSearchParams()
    formData.append('username', username)
    formData.append('password', password)

    let authDomain = await GetAPIDomain()

    const response = await fetch(authDomain + '/connect/log-in', {
        body: JSON.stringify({
            email: username,
            password: password
        }),
        method: 'POST',
        headers: {
            'x-account-id': accountId ?? "",
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    })

    const data = await response.json()

    if (response.ok) {
        localStorage.setItem('domain-auth', authDomain || '')
        localStorage.setItem('cookie-auth', "true")
        localStorage.removeItem(ENTRA_SSO_FLAG)
        // Clear any project-guest marker left behind by an earlier guest session in this
        // browser — otherwise ApiClient's 401 handler would send this CRM user to
        // /projects/guest/login. Only ProjectGuestLogin sets it.
        localStorage.removeItem('is-project-guest')
        return {
            successful: true,
        }
    }

    let tId = localStorage.getItem('tenantAccountId');
    localStorage.clear()
    if (tId)
        localStorage.setItem('tenantAccountId', tId);
    return {
        successful: false,
        error: data.error,
        error_description: data.error_description
    }
}

export async function checkUserAccess(email: string, tenantId?: string) {
    var response = await fetch((await GetAPIDomain()) + `/users/check-user-access?email=${encodeURIComponent(email)}`, {
        headers: {
            'x-account-id': tenantId ?? "",
        },
    })

    const data = await response.json()

    return data
}

export async function ssoLogin(accountId: string, openIdToken: string) {
    let formData = new URLSearchParams()
    formData.append('grant_type', 'open_id')
    formData.append('account_id', accountId)
    formData.append('client_id', '4d2c5652-c3e0-443f-b317-ed1ba44a86bc')
    formData.append('open_id_token', openIdToken)

    localStorage.setItem('accountId', accountId)
    localStorage.setItem('tenantAccountId', accountId)

    var domain = await GetAPIDomain()

    const response = await fetch(domain + '/connect/token', {
        body: formData,
        method: 'POST',
        headers: getConnectTokenHeaders(accountId),
    })

    const data = await response.json()

    if (response.ok) {
        localStorage.setItem('api-domain', domain || '')
        localStorage.setItem('acess-token', data.access_token)
        localStorage.setItem('refresh-token', data.refresh_token)
        storeTokenExpiry(data.expires_in)
        return true
    }

    localStorage.clear()
    return false
}

/** CRM SSO: store Entra access/refresh tokens for direct API Bearer auth. */
export async function applyEntraAuthSession(
    accountId: string,
    tokens: EntraTokenResponse,
    entraConfig: {tenantId: string; clientId: string; scope: string}
) {
    const domain = await GetAPIDomain()

    localStorage.setItem('accountId', accountId)
    localStorage.setItem('tenantAccountId', accountId)
    localStorage.setItem('api-domain', domain || '')
    localStorage.removeItem('cookie-auth')
    // Same reasoning as login() above — an SSO sign-in is never a project guest.
    localStorage.removeItem('is-project-guest')
    localStorage.setItem(ENTRA_SSO_FLAG, 'true')
    localStorage.setItem('entra-tenant-id', entraConfig.tenantId)
    localStorage.setItem('entra-client-id', entraConfig.clientId)
    localStorage.setItem('entra-scope', entraConfig.scope)
    persistEntraTokens(tokens)
}

// Server should return AuthModel
export function register(email: string, firstname: string, lastname: string, password: string) {
    return axios.post<AuthModel>(REGISTER_URL, {
        email,
        firstname,
        lastname,
        password,
    })
}

export function LogOut() {
    localStorage.removeItem('acess-token')
    localStorage.removeItem('refresh-token')
    localStorage.removeItem('is-on-web')

    localStorage.clear()
}

export function IsAuthenticated() {
    if (localStorage.getItem('acess-token') == null) return false

    var token = localStorage.getItem('acess-token')
    if (token != null && token.length === 0) return false

    return true
}

export async function RefreshToken() {
    if (localStorage.getItem(ENTRA_SSO_FLAG) === 'true') {
        return refreshEntraToken()
    }

    return refreshFirstPartyToken()
}

async function refreshEntraToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh-token')
    const tenantId = localStorage.getItem('entra-tenant-id')
    const clientId = localStorage.getItem('entra-client-id')
    const scope = localStorage.getItem('entra-scope') || ''

    if (!refreshToken || !tenantId || !clientId) {
        return beginSilentEntraSignIn()
    }

    try {
        const tokens = await refreshEntraAccessToken({
            tenantId,
            clientId,
            refreshToken,
            scope,
        })
        persistEntraTokens(tokens)
        return true
    } catch {
        return beginSilentEntraSignIn()
    }
}

async function refreshFirstPartyToken(): Promise<boolean> {
    var token = localStorage.getItem('refresh-token')

    if (!token) {
        localStorage.clear()
        return false;
    }

    let formData = new URLSearchParams()
    formData.append('refresh_token', token)
    formData.append('client_id', '4d2c5652-c3e0-443f-b317-ed1ba44a86bc')
    formData.append('grant_type', 'refresh_token')

    const accountId =
        localStorage.getItem('accountId') || localStorage.getItem('tenantAccountId') || ''

    const domain = localStorage.getItem('api-domain') || (await GetAPIDomain())

    const response = await fetch(`${domain.replace(/\/$/, '')}/connect/token`, {
        body: formData,
        method: 'POST',
        headers: getConnectTokenHeaders(accountId),
    })

    if (!response.ok) {
        localStorage.clear()
        return false;
    }

    const data = await response.json()

    localStorage.setItem('acess-token', data.access_token)
    localStorage.setItem('refresh-token', data.refresh_token)
    storeTokenExpiry(data.expires_in)

    return true;
}

// Server should return object => { result: boolean } (Is Email in DB)
export function requestPassword(email: string) {
    return axios.post<{ result: boolean }>(REQUEST_PASSWORD_URL, { email })
}

export async function discoverCurrentAccount(email: string) {
    var response = await fetch((await GetAPIDomain()) + `/discovery/current?email=${encodeURIComponent(email)}`, {
        headers: {
            'x-account-discovery': 'x-account-discovery',
        },
    })

    const data = await response.json()
    if (response.ok) {
        localStorage.setItem('accountId', data.id)
    }

    return { ok: response.ok, data: data }
}

export async function discoverTenantSettingsAccount(shortCode: string) {
    var response = await fetch((await GetAPIDomain()) + `/discovery/lookup/${encodeURIComponent(shortCode)}`, {
        headers: {
            'x-account-discovery': 'x-account-discovery',
        },
    })

    const data = await response.json()
    if (response.ok) {
        localStorage.setItem('accountId', data.id)
    }

    return { ok: response.ok, data: data }
}

export async function getssoLink(accountId: string, email: string) {
    const nonce = Math.random().toString(36).slice(2, 7)

    let localAccountId = localStorage.getItem('accountId')
    let apiDomain = await GetAPIDomain()

    return await ApiResponse.Create(() =>
        fetch(apiDomain + `/accounts/${accountId}/sign-in-link?nonce=${nonce}&prompt=select_account&login_hint=${email}`, {
            headers: {
                'x-account-id': localAccountId || '',
            },
        })
    )
}

export async function getUserByToken() {
    // Authorization head should be fulfilled in interceptor.
    // Check common redux folder => setupAxios
    const domain = localStorage.getItem('api-domain')
    let response = await httpClientWithAuth.get(domain + `/users/by-token`)

    const { data } = response
    if (!data.hasCrmAccess) {
        if (data.isProjectGuest) {
            // No CRM access, but a project guest belongs in the portal, not /no-access.
            return response
        }
        window.location.href = '/no-access'
    } else {
        return response
    }
}

export function getUserPermissionByToken() {
    const domain = localStorage.getItem('api-domain')
    return httpClientWithAuth.get(domain + `/permissions/for/current-user`)
}
